import {
  ICardDetails,
  ICardResponse
} from "@wingspanhq/bookkeeping/dist/lib/interfaces";
import {
  BulkStatus,
  ClientWorkFlowStatus,
  FrequencyAndScheduleStatus,
  IAdditionalData,
  IBankingApplicationForm,
  IBankStatement,
  IBulkBatchCreate,
  IBulkBatchUpdate,
  IBulkCollaboratorBatch,
  IBulkCollaboratorItem,
  IBulkCollaboratorItemCreate,
  IBulkCollaboratorItemUpdate,
  IBulkPayableBatch,
  IBulkPayableBatchCreate,
  IBulkPayableBatchUpdate,
  IBulkPayableItem,
  IBulkPayableItemCreate,
  IBulkPayableItemUpdate,
  ICalculate1099Request,
  IClientInvoice,
  IClientInvoiceCreateRequest,
  IClientInvoiceTemplate,
  IClientInvoiceTemplateCreateRequest,
  IClientInvoiceTemplateUpdateRequest,
  IClientInvoiceUpdateRequest,
  ICollaboratorCreateRequest,
  ICollaboratorEvents,
  ICollaboratorGroupResponse,
  ICollaboratorSchema,
  ICollaboratorUpdateRequest,
  IEligibilityRequirementCreateRequest,
  IInstantPayoutRequest,
  IInstantPayoutResponse,
  IInstitutionResponse,
  IInvoice,
  IInvoiceCreateRequest,
  IInvoiceFeeCalculation,
  IInvoiceTemplate,
  IInvoiceTemplateCreateRequest,
  IInvoiceTemplateUpdateRequest,
  IInvoiceUpdateRequest,
  IMccResponse,
  IMemberClient,
  IMemberClientCreateRequest,
  IMemberClientForm1099Balances,
  IMemberClientFormW9Info,
  IMemberClientUpdateRequest,
  InvoiceStatus,
  IPayableCreateRequest,
  IPayableSchema,
  IPayablesSummary,
  IPayableUpdateRequest,
  IPayoutDestinationUpdate,
  IPayRequest,
  IPayrollSettings,
  IPayrollSettingsUpdate,
  IServiceStatusResponse,
  IServiceUpdateRequest,
  MemberClientStatus,
  MemberClientTaxStatus,
  MemberWorkFlowStatus,
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import {
  IBulkCalculation1099ItemCreate,
  IBulkCalculation1099ItemUpdate
} from "@wingspanhq/payments/dist/interfaces/api/bulkCalculation1099";
import {
  ICardCodeRequest,
  ICardCodeResponse,
  ICardCreateRequest,
  ICardTokenRequest,
  ICardTokenResponse,
  ICardUpdateRequest
} from "@wingspanhq/payments/dist/interfaces/api/card";
import {
  ICollaboratorGroupCreateRequest,
  ICollaboratorGroupRequirementUpdate,
  ICollaboratorGroupUpdateRequest
} from "@wingspanhq/payments/dist/interfaces/api/collaboratorGroup";
import {
  IDeductionCreateRequest,
  IDeductionResponse,
  IDeductionUpdateRequest
} from "@wingspanhq/payments/dist/interfaces/api/deductions";
import { IPayoutSettingsResponse } from "@wingspanhq/payments/dist/interfaces/api/payoutSettings";
import {
  IBulkCalculation1099Batch,
  IBulkCalculation1099Item
} from "@wingspanhq/payments/dist/interfaces/bulkCalculation1099";
import { IEligibilityRequirement } from "@wingspanhq/payments/dist/interfaces/eligibilityRequirement";
import { IRedactedMember } from "@wingspanhq/users";
import { getChangedData } from "../utils/getChangedData";
import { ListRequestQuery } from "../utils/serviceHelper";
import { WSService } from "../utils/WSService";

const service = new WSService("/payments");

/* Service */

const getService = async (): Promise<IServiceStatusResponse> => {
  const { data } = await service.get("/service");
  return data;
};

const updateService = async (
  userId: string,
  requestData: IServiceUpdateRequest
): Promise<IServiceStatusResponse> => {
  const { data } = await service.patch(`/service/${userId}`, requestData);

  return data;
};

/* MCC */

const getMCCs = async (): Promise<IMccResponse[]> => {
  const { data } = await service.get("/mcc");
  return data;
};

/* Member Client */

export interface MemberClientFilter {
  memberId?:
    | string
    | {
        "!=": string;
      };
  clientId?:
    | string
    | {
        "!=": string;
      };
  name?:
    | string
    | {
        contains: string;
      };
  company?:
    | string
    | {
        contains: string;
      };
  emailTo?:
    | string
    | {
        contains: string;
      };
  status?:
    | MemberClientStatus
    | {
        in?: MemberClientStatus[];
      };
}

const getMemberClients = async (
  query?: ListRequestQuery<
    MemberClientFilter,
    {
      updatedAt?: "asc" | "desc";
    }
  >
): Promise<IMemberClient[]> => {
  const { data } = await service.get("/memberClient", {
    params: query
  });
  return data;
};

const getMemberClient = async (id: string): Promise<IMemberClient> => {
  const { data } = await service.get(`/memberClient/${id}`);
  return data;
};

const createMemberClient = async (
  requestData: IMemberClientCreateRequest
): Promise<IMemberClient> => {
  const { data } = await service.post("/memberClient", requestData);
  return data;
};

const updateMemberClient = async (
  id: string,
  requestData: IMemberClientUpdateRequest
): Promise<IMemberClient> => {
  const { data } = await service.patch(`/memberClient/${id}`, requestData);
  return data;
};

const deleteMemberClient = async (id: string): Promise<IMemberClient> => {
  const { data } = await service.delete(`/memberClient/${id}`);
  return data;
};

/* Collaborator */

export interface CollaboratorFilter {
  memberId?:
    | string
    | {
        "!=": string;
      };
  clientId?:
    | string
    | {
        "!=": string;
      };
  name?:
    | string
    | {
        contains: string;
      };
  company?:
    | string
    | {
        contains: string;
      };
  emailTo?:
    | string
    | {
        contains: string;
      };
  status?:
    | MemberClientStatus
    | {
        in?: MemberClientStatus[];
      };
  taxStatus?: {
    "="?: MemberClientTaxStatus;
    "!="?: MemberClientTaxStatus;
  };
}

const getCollaborators = async (
  query?: ListRequestQuery<
    CollaboratorFilter,
    {
      updatedAt?: "asc" | "desc";
    }
  >
): Promise<ICollaboratorSchema[]> => {
  const { data } = await service.get("/collaborator", {
    params: query
  });
  return data;
};

const getCollaboratorsListSize = async (
  query?: ListRequestQuery<
    CollaboratorFilter,
    {
      updatedAt?: "asc" | "desc";
    }
  >
): Promise<number> => {
  const { headers } = await service.get("/collaborator", {
    params: { ...query, page: { size: 1 } }
  });
  return Number(headers["x-ws-list-size"]);
};

const getCollaborator = async (id: string): Promise<ICollaboratorSchema> => {
  const { data } = await service.get(`/collaborator/${id}`);
  return data;
};

const getCollaboratorEvents = async (
  id: string
): Promise<ICollaboratorEvents> => {
  const { data } = await service.get(`/collaborator/${id}/events`);
  return data;
};

const createCollaborator = async (
  requestData: ICollaboratorCreateRequest
): Promise<ICollaboratorSchema> => {
  const { data } = await service.post("/collaborator", requestData);
  return data;
};

const updateCollaborator = async (
  id: string,
  requestData: ICollaboratorUpdateRequest
): Promise<ICollaboratorSchema> => {
  const { data } = await service.patch(`/collaborator/${id}`, requestData);
  return data;
};

export interface ICollaboratorPatchRequest {
  form1099Balances?: {
    2022?: Partial<IMemberClientForm1099Balances>;
  };
  formW9Data?: Partial<IMemberClientFormW9Info>;
  // member: IRedactedMember;
}

const patchCollaborator = async (
  id: string,
  requestData: ICollaboratorPatchRequest
): Promise<ICollaboratorSchema> => {
  const { data } = await service.patch(`/collaborator/${id}`, requestData);
  return data;
};

const deleteCollaborator = async (id: string): Promise<ICollaboratorSchema> => {
  const { data } = await service.delete(`/collaborator/${id}`);
  return data;
};

const download1099 = async (id: string): Promise<Blob> => {
  const { data: blob } = await service.get(
    `/collaborator/${id}/download-1099`,
    {
      responseType: "blob"
    }
  );
  return blob;
};

const downloadW9 = async (id: string): Promise<Blob> => {
  const { data: blob } = await service.get(`/collaborator/${id}/download-w9`, {
    responseType: "blob"
  });
  return blob;
};

/* Collaborator group */

const getCollaboratorGroups = async (
  params: any
): Promise<ICollaboratorGroupResponse[]> => {
  const { data } = await service.get("/collaborator-group", { params });
  return data;
};

const getCollaboratorGroup = async (
  collaboratorGroupId: string
): Promise<ICollaboratorGroupResponse> => {
  const { data } = await service.get(
    "/collaborator-group/" + collaboratorGroupId
  );
  return data;
};

const createCollaboratorGroup = async (
  params: ICollaboratorGroupCreateRequest
): Promise<ICollaboratorGroupResponse> => {
  const { data } = await service.post("/collaborator-group", params);
  return data;
};

const updateCollaboratorGroup = async (
  id: string,
  params: ICollaboratorGroupUpdateRequest
): Promise<ICollaboratorGroupResponse> => {
  const { data } = await service.patch(`/collaborator-group/${id}`, params);
  return data;
};

const deleteCollaboratorGroup = async (
  id: string
): Promise<ICollaboratorGroupResponse> => {
  const { data } = await service.delete(`/collaborator-group/${id}`);
  return data;
};

const deleteCollaboratorGroupRequirement = async (
  collaboratorGroupId: string,
  eligibilityRequirementId: string
): Promise<ICollaboratorGroupResponse> => {
  const { data } = await service.delete(
    `/collaborator-group/${collaboratorGroupId}/eligibility-requirement/${eligibilityRequirementId}`
  );
  return data;
};

const replaceCollaboratorGroupRequirement = async (
  collaboratorGroupId: string,
  eligibilityRequirementId: string,
  params: ICollaboratorGroupRequirementUpdate
): Promise<ICollaboratorGroupResponse> => {
  const { data } = await service.patch(
    `/collaborator-group/${collaboratorGroupId}/eligibility-requirement/${eligibilityRequirementId}`,
    params
  );
  return data;
};
/* Invoice */

export interface InvoicesFilter {
  memberId?: string;
  clientId?: string;
  memberClientId?:
    | string
    | {
        in?: string[];
      };
  status?:
    | InvoiceStatus
    | {
        "!="?: InvoiceStatus;
        in?: InvoiceStatus[];
      };
  "client.workflowStatus"?:
    | ClientWorkFlowStatus
    | {
        "!="?: ClientWorkFlowStatus;
        in?: ClientWorkFlowStatus[];
        notIn?: ClientWorkFlowStatus[];
      };
  "member.workflowStatus"?:
    | MemberWorkFlowStatus
    | {
        "!="?: MemberWorkFlowStatus;
        in?: MemberWorkFlowStatus[];
        notIn?: MemberWorkFlowStatus[];
      };
  dueDate?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "events.openedAt"?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "events.paidAt"?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  createdAt?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  updatedAt?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "labels.invoiceType"?:
    | string
    | {
        "!=": string;
      };
  "labels.creationSource"?:
    | string
    | {
        "!=": string;
      };
  "internal.collaborators.parentInvoiceId"?: string;
}

const getInvoices = async (
  query?: ListRequestQuery<
    InvoicesFilter,
    {
      updatedAt: "asc" | "desc";
    }
  >
): Promise<IInvoice[]> => {
  const { data } = await service.get("/invoice", { params: query });
  return data;
};

const getInvoice = async (id: string): Promise<IInvoice> => {
  const { data } = await service.get(`/invoice/${id}`);
  return data;
};

const createInvoice = async (
  requestData: IInvoiceCreateRequest
): Promise<IInvoice> => {
  const { data } = await service.post("/invoice", requestData);
  return data;
};

const updateInvoice = async (
  id: string,
  requestData: IInvoiceUpdateRequest
): Promise<IInvoice> => {
  const { data } = await service.patch(`/invoice/${id}`, requestData);
  return data;
};

const sendInvoiceReminder = async (id: string) => {
  const { data } = await service.post(`/invoice/${id}/send`);
  return data;
};

const generateInvoicePdf = async (id: string) => {
  const { data } = await service.post(`/invoice/${id}/generate`);
  return data;
};

const deleteInvoice = async (id: string) => {
  const { data } = await service.delete(`/invoice/${id}`);
  return data;
};

/* Payble */

export interface PayablesFilter {
  memberId?: string;
  clientId?: string;
  memberClientId?:
    | string
    | {
        in?: string[];
      };
  status?:
    | InvoiceStatus
    | {
        "!="?: InvoiceStatus;
        in?: InvoiceStatus[];
      };
  "client.workflowStatus"?:
    | ClientWorkFlowStatus
    | {
        "!="?: ClientWorkFlowStatus;
        in?: ClientWorkFlowStatus[];
        notIn?: ClientWorkFlowStatus[];
      };
  "member.workflowStatus"?:
    | MemberWorkFlowStatus
    | {
        "!="?: MemberWorkFlowStatus;
        in?: MemberWorkFlowStatus[];
        notIn?: MemberWorkFlowStatus[];
      };
  dueDate?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "events.openedAt"?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "events.paidAt"?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  createdAt?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  updatedAt?: {
    [key in "=" | ">" | ">=" | "<" | "<="]?: Date;
  };
  "labels.invoiceType"?:
    | string
    | {
        "!=": string;
      };
  "internal.collaborators.parentInvoiceId"?: string;
}

export type PayablesWithSummary = {
  data: IPayableSchema[];
  summary: {
    listSize: number;
    totalValue: number;
  };
};

const getPayables = async (
  query?: ListRequestQuery<
    PayablesFilter,
    {
      updatedAt?: "asc" | "desc";
      "events.paidAt"?: "asc" | "desc";
    }
  >
): Promise<PayablesWithSummary> => {
  const { headers, data } = await service.get("/payable", { params: query });

  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"]),
      totalValue: Number(headers["x-ws-custom-invoice-amount-total"])
    }
  };
};

const getPayable = async (id: string): Promise<IPayableSchema> => {
  const { data } = await service.get(`/payable/${id}`);
  return data;
};

const createPayable = async (
  requestData: IPayableCreateRequest
): Promise<IPayableSchema> => {
  const { data } = await service.post("/payable", requestData);
  return data;
};

const updatePayable = async (
  id: string,
  requestData: IPayableUpdateRequest
): Promise<IPayableSchema> => {
  const { data } = await service.patch(`/payable/${id}`, requestData);
  return data;
};
const deletePayable = async (id: string) => {
  const { data } = await service.delete(`/payable/${id}`);
  return data;
};

/* Invoice template */

export interface InvoiceTemplatesFilter {
  memberId?: string;
  status?:
    | FrequencyAndScheduleStatus
    | {
        "!="?: FrequencyAndScheduleStatus;
        in?: FrequencyAndScheduleStatus[];
      };
}

const getInvoiceTemplates = async (
  query?: ListRequestQuery<
    InvoiceTemplatesFilter,
    {
      updatedAt: "asc" | "desc";
    }
  >
): Promise<IInvoiceTemplate[]> => {
  const response = await service.get("/invoice-template", { params: query });
  return response.data;
};

const getInvoiceTemplate = async (id: string): Promise<IInvoiceTemplate> => {
  const response = await service.get(`/invoice-template/${id}`);
  return response.data;
};

const createInvoiceTemplate = async (
  data: IInvoiceTemplateCreateRequest
): Promise<IInvoiceTemplate> => {
  const response = await service.post("/invoice-template", data);
  return response.data;
};

const updateInvoiceTemplate = async (
  id: string,
  data: IInvoiceTemplateUpdateRequest
): Promise<IInvoiceTemplate> => {
  const response = await service.patch(`/invoice-template/${id}`, data);
  return response.data;
};

const deleteInvoiceTemplate = async (id: string): Promise<IInvoiceTemplate> => {
  const response = await service.delete(`/invoice-template/${id}`);
  return response.data;
};

/* Client Invoice */

const getClientInvoices = async (
  query?: ListRequestQuery<
    InvoicesFilter,
    {
      updatedAt: "asc" | "desc";
    }
  >
): Promise<IClientInvoice[]> => {
  const response = await service.get("/client/invoice", {
    params: query
  });
  return response.data;
};

const getClientInvoice = async (id: string): Promise<IClientInvoice> => {
  const response = await service.get(`/client/invoice/${id}`);
  return response.data;
};

const createClientInvoice = async (
  data: IClientInvoiceCreateRequest
): Promise<IClientInvoice> => {
  const response = await service.post("/client/invoice", data);
  return response.data;
};

const updateClientInvoice = async (
  id: string,
  data: IClientInvoiceUpdateRequest
): Promise<IClientInvoice> => {
  const response = await service.patch(`/client/invoice/${id}`, data);
  return response.data;
};

const getClientInvoiceFees = async (
  id: string
): Promise<IInvoiceFeeCalculation> => {
  const response = await service.get(`/client/invoice/${id}/fees`);
  return response.data;
};

const payClientInvoice = async (
  id: string,
  data: IPayRequest
): Promise<IClientInvoice> => {
  const response = await service.post(`/client/invoice/${id}/pay`, data);
  return response.data;
};

/* Client Invoice Template */

const getClientInvoiceTemplates = async (): Promise<IClientInvoiceTemplate[]> => {
  const response = await service.get("/client/invoice-template");
  return response.data;
};

const getClientInvoiceTemplate = async (
  id: string
): Promise<IClientInvoiceTemplate> => {
  const response = await service.get(`/client/invoice-template/${id}`);
  return response.data;
};

const createClientInvoiceTemplate = async (
  data: IClientInvoiceTemplateCreateRequest
): Promise<IClientInvoiceTemplate> => {
  const response = await service.post("/client/invoice-template", data);
  return response.data;
};

const updateClientInvoiceTemplate = async (
  id: string,
  data: IClientInvoiceTemplateUpdateRequest
): Promise<IClientInvoiceTemplate> => {
  const response = await service.patch(`/client/invoice-template/${id}`, data);
  return response.data;
};

/* Banking Statement */

const listBankingStatements = async (): Promise<IBankStatement[]> => {
  const response = await service.get("/banking/statement");
  return response.data;
};

const getBankingStatement = async (
  statementId: string
): Promise<IBankStatement> => {
  const response = await service.get(`/banking/statement/${statementId}`);
  return response.data;
};

const getBankingApplication = async (
  userId: string
): Promise<IBankingApplicationForm> => {
  const response = await service.get(`/service/banking/${userId}/application`);
  return response.data;
};

/* Instant payout */

const getInstantPayout = async (): Promise<IInstantPayoutResponse> => {
  const response = await service.get("/banking/instant-payout");
  return response.data;
};

const createInstantPayout = async (
  data: IInstantPayoutRequest
): Promise<IInstantPayoutResponse> => {
  const response = await service.post("/banking/instant-payout", data);
  return response.data;
};

/* Collaborator settings */
const getCollaboratorSettingsAdditionalData = async (): Promise<Array<
  IAdditionalData
>> => {
  const response = await service.get("/collaborator-settings/additional-data");
  return response.data;
};

const getCollaboratorSettingsAdditionalDataItem = async (id: string) => {
  const response = await service.get(
    `/collaborator-settings/additional-data/${id}`
  );
  return response.data as IAdditionalData & { id: string };
};

const createCollaboratorSettingsAdditionalData = async (
  data: IAdditionalData
) => {
  const response = await service.post(
    "/collaborator-settings/additional-data",
    data
  );
  return response.data as IAdditionalData;
};

const updateCollaboratorSettingsAdditionalData = async (payload: {
  id: string;
  data: IAdditionalData;
}) => {
  const response = await service.patch(
    `/collaborator-settings/additional-data/${payload.id}`,
    payload.data
  );
  return response.data as IAdditionalData;
};

const deleteCollaboratorSettingsAdditionalData = async (id: string) => {
  const response = await service.delete(
    `/collaborator-settings/additional-data/${id}`
  );
  return response.data;
};

/* Eligibility requirements */

export interface EligibilityRequirementFilter {
  eligibilityRequirementId?: {
    in: string[];
  };
}

const getEligibilityRequirements = async (
  query?: ListRequestQuery<EligibilityRequirementFilter>
): Promise<IEligibilityRequirement[]> => {
  const response = await service.get(
    "/collaborator-settings/eligibility-requirement",
    { params: query }
  );
  return response.data;
};

const getEligibilityRequirement = async (
  id: string
): Promise<IEligibilityRequirement> => {
  const response = await service.get(
    "/collaborator-settings/eligibility-requirement/" + id
  );
  return response.data;
};

const createEligibilityRequirement = async (
  requestData: IEligibilityRequirementCreateRequest
): Promise<IEligibilityRequirement> => {
  const response = await service.post(
    "/collaborator-settings/eligibility-requirement",
    requestData
  );
  return response.data;
};

const deleteEligibilityRequirement = async (
  id: string
): Promise<IEligibilityRequirement> => {
  const response = await service.delete(
    "/collaborator-settings/eligibility-requirement/" + id
  );
  return response.data;
};

/* Payables summary */

const getPayablesSummary = async (): Promise<IPayablesSummary> => {
  const response = await service.get("/summary/payables");
  return response.data;
};

/* Payroll settings */
const getPayrollSettings = async (
  memberId: string
): Promise<IPayrollSettings> => {
  const response = await service.get(`/payroll-settings/${memberId}`);
  return response.data;
};

const updatePayrollSettings = async (
  memberId: string,
  data: IPayrollSettingsUpdate
): Promise<IPayrollSettings> => {
  const response = await service.patch(`/payroll-settings/${memberId}`, data);
  return response.data;
};

/* Cards  */

const createCard = async (data: ICardCreateRequest): Promise<ICardResponse> => {
  const response = await service.post("/banking/card", data);
  return response.data;
};

const replaceCard = async (payload: {
  id: string;
  data: ICardCreateRequest;
}): Promise<ICardResponse> => {
  const response = await service.post(
    `/banking/card/${payload.id}/replace`,
    payload.data
  );
  return response.data;
};

const getCards = async (): Promise<ICardResponse[]> => {
  const response = await service.get("/banking/card");
  return response.data;
};

export type ICardDetailsResponse = {
  cardId: string;
  memberId: string;
} & ICardDetails;

const getCard = async (id: string): Promise<ICardDetailsResponse> => {
  const response = await service.get(`/banking/card/${id}`);
  return response.data;
};

const updateCard = async (
  id: string,
  data: ICardUpdateRequest
): Promise<ICardResponse> => {
  const response = await service.patch(`/banking/card/${id}`, data);
  return response.data;
};

const deleteCard = async (id: string): Promise<ICardResponse> => {
  const response = await service.delete(`/banking/card/${id}`);
  return response.data;
};

/* Card token */

const createCardToken = async (
  id: string,
  data: ICardCodeRequest
): Promise<ICardCodeResponse> => {
  const response = await service.post(`/banking/card/${id}/token`, data);
  return response.data;
};

const updateCardToken = async (
  id: string,
  data: ICardTokenRequest
): Promise<ICardTokenResponse> => {
  const response = await service.patch(`/banking/card/${id}/token`, data);
  return response.data;
};

/* Payout Settings */

const getPayoutSettings = async (
  memberId: string
): Promise<IPayoutSettingsResponse> => {
  const response = await service.get(`/payout-settings/${memberId}`);
  return response.data;
};

const updatePayoutSettings = async (
  memberId: string,
  updateData: {
    payoutPreferences?: PayoutPreferences;
    standard?: {
      destinationType: PayoutDestinationType;
      percentage?: number;
      destinationId: string;
    } | null;
    instant?: {
      destinationType: PayoutDestinationType;
      percentage?: number;
      destinationId: string;
    } | null;
  }
): Promise<IPayoutSettingsResponse> => {
  const payoutSettings = await getPayoutSettings(memberId);

  const payoutDestinationsUpdate: IPayoutDestinationUpdate[] = payoutSettings.payoutDestinations.map(
    _ => ({})
  );

  // Update standard destination
  if (updateData.standard !== undefined) {
    const indexOfCurrentStandardPayoutMethod = payoutSettings.payoutDestinations.findIndex(
      d => d.payoutMethod === PayoutPreferences.Standard
    );

    if (indexOfCurrentStandardPayoutMethod >= 0) {
      if (updateData.standard === null) {
        // Delete standard destination
        payoutDestinationsUpdate[
          indexOfCurrentStandardPayoutMethod
        ] = null as any;
      } else {
        // Update standard destination
        payoutDestinationsUpdate[
          indexOfCurrentStandardPayoutMethod
        ] = getChangedData(
          payoutSettings.payoutDestinations[indexOfCurrentStandardPayoutMethod],
          updateData.standard
        );
      }
    } else {
      // Create a standard destination
      if (updateData.standard !== null) {
        payoutDestinationsUpdate.push({
          payoutMethod: PayoutPreferences.Standard,
          percentage: 100,
          ...updateData.standard
        });
      }
    }
  }

  // Update instant destination
  if (updateData.instant !== undefined) {
    const indexOfCurrentInstantPayoutMethod = payoutSettings.payoutDestinations.findIndex(
      d => d.payoutMethod === PayoutPreferences.Instant
    );

    if (indexOfCurrentInstantPayoutMethod >= 0) {
      if (updateData.instant === null) {
        // Delete instant destination
        payoutDestinationsUpdate[
          indexOfCurrentInstantPayoutMethod
        ] = null as any;
      } else {
        // Update instant destination
        payoutDestinationsUpdate[
          indexOfCurrentInstantPayoutMethod
        ] = getChangedData(
          payoutSettings.payoutDestinations[indexOfCurrentInstantPayoutMethod],
          updateData.instant
        );
      }
    } else {
      // Create instant destination
      if (updateData.instant !== null) {
        payoutDestinationsUpdate.push({
          payoutMethod: PayoutPreferences.Instant,
          percentage: 100,
          ...updateData.instant
        });
      }
    }
  }

  const response = await service.patch(`/payout-settings/${memberId}`, {
    payoutPreferences: updateData.payoutPreferences,
    payoutDestinations: payoutDestinationsUpdate
  });

  return response.data as IPayoutSettingsResponse;
};

const updateWalletDistribution = async (
  memberId: string,
  walletDistributionPercentage: number
): Promise<IPayoutSettingsResponse> => {
  const payoutSettings = await getPayoutSettings(memberId);

  const payoutDestinationsUpdate: (IPayoutDestinationUpdate | null)[] = payoutSettings.payoutDestinations.map(
    _ =>
      walletDistributionPercentage === 100
        ? null
        : {
            percentage: 100 - walletDistributionPercentage
          }
  );

  const response = await service.patch(`/payout-settings/${memberId}`, {
    payoutDestinations: payoutDestinationsUpdate
  });

  return response.data as IPayoutSettingsResponse;
};

// TODO: remove these interfaces and replace with those in payments

export enum CardBrand {
  AmericanExpress = "AmericanExpress",
  Visa = "Visa",
  Mastercard = "Mastercard",
  Discover = "Discover",
  DinersClub = "DinersClub",
  JCB = "JCB"
}

export interface IAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country: string;
}

export interface ICheckbookCard {
  cardId: string;
  brand: CardBrand;
  last4: string;
  expirationYYYY: string;
  expirationMM: string;
  address: IAddress;
}

export interface ICheckbookCardCreate {
  cardNumber: string;
  expYYYY: string;
  expMM: string;
  cvv?: string;
  name: string;
  address?: IAddress;
}

// -- remove meeeee ^ --

const getPayoutSettingsDebitCards = async (
  memberId: string
): Promise<ICheckbookCard[]> => {
  const response = await service.get(`/payout-settings/${memberId}/debit-card`);
  return response.data;
};

const getPayoutSettingsDebitCard = async (
  memberId: string,
  debitCardId: string
): Promise<ICheckbookCard> => {
  const response = await service.get(
    `/payout-settings/${memberId}/debit-card/${debitCardId}`
  );
  if (response.data?.length > 0) {
    return response.data[0];
  }
  return response.data;
};

const createPayoutSettingsDebitCard = async (
  memberId: string,
  data: ICheckbookCardCreate
): Promise<ICheckbookCard> => {
  const response = await service.post(
    `/payout-settings/${memberId}/debit-card`,
    data
  );
  return response.data;
};

const deletePayoutSettingsDebitCard = async (
  memberId: string,
  debitCardId: string
): Promise<ICheckbookCard> => {
  const response = await service.delete(
    `/payout-settings/${memberId}/debit-card/${debitCardId}`
  );
  return response.data;
};

/* Payout Settings */

const getInstitution = async (id: string): Promise<IInstitutionResponse> => {
  const response = await service.get(`/banking/institution/${id}`);
  return response.data;
};

/* Deductions */

const createClientDeduction = async (
  data: IDeductionCreateRequest
): Promise<IDeductionResponse> => {
  const response = await service.post(`/client-deduction`, data);
  return response.data;
};

const getClientDeductions = async (
  params: any
): Promise<IDeductionResponse[]> => {
  const response = await service.get(`/client-deduction`, { params });
  return response.data;
};

const getClientDeduction = async (id: string): Promise<IDeductionResponse> => {
  const response = await service.get(`/client-deduction/${id}`);
  return response.data;
};

const updateClientDeduction = async (
  id: string,
  data: IDeductionUpdateRequest
): Promise<IDeductionResponse> => {
  const response = await service.patch(`/client-deduction/${id}`, data);
  return response.data;
};

const deleteClientDeduction = async (
  id: string
): Promise<IDeductionResponse> => {
  const response = await service.delete(`/client-deduction/${id}`);
  return response.data;
};

const createCollaboratorDeduction = async (
  data: IDeductionCreateRequest
): Promise<IDeductionResponse> => {
  const response = await service.post(`/collaborator-deduction`, data);
  return response.data;
};

const getCollaboratorDeductions = async (
  params: any
): Promise<IDeductionResponse[]> => {
  const response = await service.get(`/collaborator-deduction`, { params });
  return response.data;
};

const getCollaboratorDeduction = async (
  id: string
): Promise<IDeductionResponse> => {
  const response = await service.get(`/collaborator-deduction/${id}`);
  return response.data;
};

const updateCollaboratorDeduction = async (
  id: string,
  data: IDeductionUpdateRequest
): Promise<IDeductionResponse> => {
  const response = await service.patch(`/collaborator-deduction/${id}`, data);
  return response.data;
};

const deleteCollaboratorDeduction = async (
  id: string
): Promise<IDeductionResponse> => {
  const response = await service.delete(`/collaborator-deduction/${id}`);
  return response.data;
};

// bulk collaborator upload

const createBulkCollaboratorBatch = async (
  data: IBulkBatchCreate
): Promise<IBulkCollaboratorBatch> => {
  const response = await service.post(`/bulk/collaborator/batch`, data);
  return response.data;
};

const updateBulkCollaboratorBatch = async (
  id: string,
  data: IBulkBatchUpdate
): Promise<IBulkCollaboratorBatch> => {
  const response = await service.patch(`/bulk/collaborator/batch/${id}`, data);
  return response.data;
};

const getBulkCollaboratorBatch = async (
  id: string
): Promise<IBulkCollaboratorBatch> => {
  const response = await service.get(`/bulk/collaborator/batch/${id}`);
  return response.data;
};

const createBulkCollaboratorBatchItem = async (
  bulkCollaboratorBatchId: string,
  data: IBulkCollaboratorItemCreate
): Promise<IBulkCollaboratorItem> => {
  const response = await service.post(
    `/bulk/collaborator/batch/${bulkCollaboratorBatchId}/item`,
    data
  );
  return response.data;
};

const updateBulkCollaboratorBatchItem = async (
  bulkCollaboratorBatchId: string,
  bulkCollaboratorItemId: string,
  data: IBulkCollaboratorItemUpdate
): Promise<IBulkCollaboratorItem> => {
  const response = await service.patch(
    `/bulk/collaborator/batch/${bulkCollaboratorBatchId}/item/${bulkCollaboratorItemId}`,
    data
  );
  return response.data;
};

const getBulkCollaboratorBatchItems = async (
  batchId: string,
  status: BulkStatus[]
): Promise<{
  data: IBulkCollaboratorItem[];
  summary: { listSize: number };
}> => {
  const { headers, data } = await service.get(
    `/bulk/collaborator/batch/${batchId}/item`,
    {
      params: { filter: { status: { in: status } } }
    }
  );
  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"])
    }
  };
};

const getBulkCollaboratorBatchItem = async (
  bulkCollaboratorBatchId: string,
  bulkCollaboratorItemId: string
): Promise<IBulkCollaboratorItem> => {
  const response = await service.get(
    `/bulk/collaborator/batch/${bulkCollaboratorBatchId}/item/${bulkCollaboratorItemId}`
  );
  return response.data;
};

// bulk payable upload

const createBulkPayableBatch = async (
  data: IBulkPayableBatchCreate
): Promise<IBulkPayableBatch> => {
  const response = await service.post(`/bulk/payable/batch`, data);
  return response.data;
};

const updateBulkPayableBatch = async (
  id: string,
  data: IBulkPayableBatchUpdate
): Promise<IBulkPayableBatch> => {
  const response = await service.patch(`/bulk/payable/batch/${id}`, data);
  return response.data;
};

const getBulkPayableBatch = async (id: string): Promise<IBulkPayableBatch> => {
  const response = await service.get(`/bulk/payable/batch/${id}`);
  return response.data;
};

const createBulkPayableBatchItem = async (
  data: IBulkPayableItemCreate
): Promise<IBulkPayableItem> => {
  const response = await service.post(
    `/bulk/payable/batch/${data.bulkPayableBatchId}/item`,
    data
  );
  return response.data;
};

const updateBulkPayableBatchItem = async (
  id: string,
  data: IBulkPayableItemUpdate
): Promise<IBulkPayableItem> => {
  const response = await service.patch(
    `/bulk/payable/batch/${data.bulkPayableBatchId}/item/${id}`,
    data
  );
  return response.data;
};

const getBulkPayableBatchItems = async (
  batchId: string,
  status: BulkStatus[]
): Promise<{ data: IBulkPayableItem[]; summary: { listSize: number } }> => {
  const { headers, data } = await service.get(
    `/bulk/payable/batch/${batchId}/item`,
    {
      params: { filter: { status: { in: status } } }
    }
  );
  return {
    data,
    summary: {
      listSize: Number(headers["x-ws-list-size"])
    }
  };
};

const getBulkPayableBatchItem = async (
  batchId: string,
  batchItemId: string
): Promise<IBulkPayableItem> => {
  const response = await service.get(
    `/bulk/payable/batch/${batchId}/item/${batchItemId}`
  );
  return response.data;
};

// 1099 NEC endpoints

// bulk calc batch
const createBulkCalculate1099Batch = async (
  data: IBulkBatchCreate
): Promise<IBulkCalculation1099Batch> => {
  const response = await service.post(`/bulk/calculation1099/batch`, data);
  return response.data;
};

const updateBulkCalculate1099Batch = async (
  id: string,
  data: IBulkBatchUpdate
): Promise<IBulkCalculation1099Batch> => {
  const response = await service.patch(
    `/bulk/calculation1099/batch/${id}`,
    data
  );
  return response.data;
};

const getBulkCalculate1099Batch = async (
  id: string
): Promise<IBulkCalculation1099Batch> => {
  const response = await service.get(`/bulk/calculation1099/batch/${id}`);
  return response.data;
};

// bulk calc batch item
const createBulkCalculate1099BatchItem = async (
  bulkCalculation1099BatchId: string,
  data: IBulkCalculation1099ItemCreate
): Promise<IBulkCalculation1099Item> => {
  const response = await service.post(
    `/bulk/calculation1099/batch/${bulkCalculation1099BatchId}/item`,
    data
  );
  return response.data;
};

const updateBulkCalculate1099BatchItem = async (
  bulkCalculation1099BatchId: string,
  bulkCalculation1099BatchItemId: string,
  data: IBulkCalculation1099ItemUpdate
): Promise<IBulkPayableItem> => {
  const response = await service.patch(
    `/bulk/calculation1099/batch/${bulkCalculation1099BatchId}/item/${bulkCalculation1099BatchItemId}`,
    data
  );
  return response.data;
};

const getBulkCalculate1099BatchItems = async (
  bulkCalculation1099BatchId: string
): Promise<IBulkPayableItem[]> => {
  const response = await service.get(
    `/bulk/calculation1099/batch/${bulkCalculation1099BatchId}/item`
  );
  return response.data;
};

const getBulkCalculate1099BatchItem = async (
  bulkCalculation1099BatchId: string,
  bulkCalculation1099BatchItemId: string
): Promise<IBulkPayableItem> => {
  const response = await service.get(
    `/bulk/calculation1099/batch/${bulkCalculation1099BatchId}/item/${bulkCalculation1099BatchItemId}`
  );
  return response.data;
};

const calculate1099NECTotal = async (
  data: ICalculate1099Request
): Promise<ICollaboratorSchema> => {
  const response = await service.post("/collaborator/1099/calculate", data);
  return response.data;
};

export const paymentsService = {
  service: {
    get: getService,
    update: updateService,
    banking: {
      application: {
        get: getBankingApplication
      }
    }
  },
  mcc: {
    list: getMCCs
  },
  memberClient: {
    list: getMemberClients,
    get: getMemberClient,
    create: createMemberClient,
    update: updateMemberClient,
    delete: deleteMemberClient
  },
  collaborator: {
    list: getCollaborators,
    listSize: getCollaboratorsListSize,
    get: getCollaborator,
    getEvents: getCollaboratorEvents,
    create: createCollaborator,
    update: updateCollaborator,
    patch: patchCollaborator,
    delete: deleteCollaborator,
    download1099: download1099,
    downloadW9: downloadW9,

    nec1099: {
      calculate1099NECTotal,
      bulkCalculation1099Batch: {
        create: createBulkCalculate1099Batch,
        update: updateBulkCalculate1099Batch,
        get: getBulkCalculate1099Batch
      },
      bulkCalculation1099BatchItem: {
        create: createBulkCalculate1099BatchItem,
        update: updateBulkCalculate1099BatchItem,
        get: getBulkCalculate1099BatchItem,
        list: getBulkCalculate1099BatchItems
      }
    }
  },
  collaboratorGroup: {
    list: getCollaboratorGroups,
    get: getCollaboratorGroup,
    create: createCollaboratorGroup,
    update: updateCollaboratorGroup,
    delete: deleteCollaboratorGroup,
    eligibilityRequirement: {
      replace: replaceCollaboratorGroupRequirement,
      delete: deleteCollaboratorGroupRequirement
    }
  },
  invoice: {
    list: getInvoices,
    get: getInvoice,
    create: createInvoice,
    update: updateInvoice,
    delete: deleteInvoice,
    sendReminder: sendInvoiceReminder,
    generatePdf: generateInvoicePdf
  },
  invoiceTemplate: {
    list: getInvoiceTemplates,
    get: getInvoiceTemplate,
    create: createInvoiceTemplate,
    update: updateInvoiceTemplate,
    delete: deleteInvoiceTemplate
  },
  payable: {
    list: getPayables,
    get: getPayable,
    create: createPayable,
    update: updatePayable,
    delete: deletePayable
  },
  client: {
    invoice: {
      list: getClientInvoices,
      get: getClientInvoice,
      create: createClientInvoice,
      update: updateClientInvoice,
      getFees: getClientInvoiceFees,
      pay: payClientInvoice
    },
    invoiceTemplate: {
      list: getClientInvoiceTemplates,
      get: getClientInvoiceTemplate,
      create: createClientInvoiceTemplate,
      update: updateClientInvoiceTemplate
    }
  },
  banking: {
    statement: {
      list: listBankingStatements,
      get: getBankingStatement
    },
    instantPayout: {
      list: getInstantPayout,
      create: createInstantPayout
    },
    card: {
      create: createCard,
      replace: replaceCard,
      get: getCard,
      list: getCards,
      update: updateCard,
      delete: deleteCard,
      token: {
        create: createCardToken,
        update: updateCardToken
      }
    },
    institution: {
      get: getInstitution
    }
  },
  collaboratorSettings: {
    list: getCollaboratorSettingsAdditionalData,
    item: getCollaboratorSettingsAdditionalDataItem,
    update: updateCollaboratorSettingsAdditionalData,
    create: createCollaboratorSettingsAdditionalData,
    delete: deleteCollaboratorSettingsAdditionalData,
    eligibilityRequirements: {
      list: getEligibilityRequirements,
      get: getEligibilityRequirement,
      create: createEligibilityRequirement,
      delete: deleteEligibilityRequirement
    }
  },
  summary: {
    payables: getPayablesSummary
  },
  payrollSettings: {
    get: getPayrollSettings,
    update: updatePayrollSettings
  },
  payoutSettings: {
    get: getPayoutSettings,
    update: updatePayoutSettings,
    updateWalletDistribution,
    debitCard: {
      get: getPayoutSettingsDebitCard,
      list: getPayoutSettingsDebitCards,
      create: createPayoutSettingsDebitCard,
      delete: deletePayoutSettingsDebitCard
    }
  },
  collaboratorDeductions: {
    list: getCollaboratorDeductions,
    get: getCollaboratorDeduction,
    update: updateCollaboratorDeduction,
    create: createCollaboratorDeduction,
    delete: deleteCollaboratorDeduction
  },
  clientDeductions: {
    list: getClientDeductions,
    get: getClientDeduction,
    update: updateClientDeduction,
    create: createClientDeduction,
    delete: deleteClientDeduction
  },
  bulkCollaborator: {
    batch: {
      create: createBulkCollaboratorBatch,
      get: getBulkCollaboratorBatch,
      update: updateBulkCollaboratorBatch
    },
    batchItem: {
      create: createBulkCollaboratorBatchItem,
      get: getBulkCollaboratorBatchItem,
      list: getBulkCollaboratorBatchItems,
      update: updateBulkCollaboratorBatchItem
    }
  },
  bulkPayable: {
    batch: {
      create: createBulkPayableBatch,
      get: getBulkPayableBatch,
      update: updateBulkPayableBatch
    },
    batchItem: {
      create: createBulkPayableBatchItem,
      get: getBulkPayableBatchItem,
      list: getBulkPayableBatchItems,
      update: updateBulkPayableBatchItem
    }
  }
};
